import React, { ReactNode, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import { ButtonIcon } from 'components/Button';
import { Loadable } from 'components/types';
import { H3, H4 } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';

interface AccordionSectionProps extends Loadable {
  actions?: ReactNode;
  afterHeaderCardRow?: boolean;
  as?: any;
  children?: ReactNode;
  className?: string;
  compact?: boolean;
  count?: number;
  filters?: React.ReactNode;
  hideButton?: boolean;
  hideContentOverflow?: boolean;
  open?: boolean;
  onOpen?(): void;
  subtitle?: string | ReactNode;
  title: string | ReactNode;
  dataCy?: string;
}

const HEADER_HEIGHT = 66;

const Layout = styled.section<Partial<AccordionSectionProps>>`
  position: relative;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  margin-bottom: ${toRem(theme.space.xl)};

  ${({ compact }) =>
    !compact &&
    css`
      padding: 0 ${toRem(theme.space.xl)};

      @media (min-width: ${theme.breakpoint.small}px) {
        padding: 0 ${toRem(theme.space['3xl'])};
      }
    `}

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }

  ${({ afterHeaderCardRow }) =>
    afterHeaderCardRow &&
    css`
      z-index: ${theme.zIndex.contentHeader + 50};
    `}
`;

const RADIUS = 11;

const Accordion = styled.div<Partial<AccordionSectionProps>>`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  border-radius: ${toRem(RADIUS)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  background-color: ${theme.color.white};
`;

const Header = styled.header<Partial<AccordionSectionProps>>`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  background-color: ${theme.color.neutral[50]};
  overflow-y: visible;
  border-radius: ${toRem(RADIUS)};

  ${({ open }) =>
    open &&
    css`
      border-top-right-radius: ${toRem(RADIUS)};
      border-top-left-radius: ${toRem(RADIUS)};
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    `}
`;

const HeaderInner = styled.div`
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: ${toRem(HEADER_HEIGHT)};
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.xl)};
  gap: ${toRem(theme.space.md)};

  > * {
    min-width: 0;
  }

  @media (max-width: ${theme.breakpoint.large}px) {
    padding: ${toRem(theme.space.md)} ${toRem(theme.space.lg)};
  }
`;

const HeaderSection = styled.section`
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  flex-grow: 1;

  ${H3} {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
  }
`;

const TitleContainer = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: auto;
  gap: ${toRem(theme.space.xs)};

  ${H4} {
    margin-top: ${toRem(theme.space.xs)};
    color: ${theme.color.neutral[800]};
  }

  .react-loading-skeleton {
    /* off-scale exception: 1px optical nudge between skeleton bars (2026-09-16) */
    margin: ${toRem(1)} 0;
  }
`;

const Actions = styled.section`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${toRem(theme.space.md)};
`;

const ToggleButton = styled(ButtonIcon)<Partial<AccordionSectionProps>>`
  margin-right: ${toRem(theme.space.md)};
  position: relative;
  transform: rotateZ(0deg);
  transition: all ${theme.transition.icon};

  svg {
    position: relative;
    top: ${toRem(1)};
    fill: ${theme.color.neutral[900]};
  }

  &:hover {
    svg {
      fill: ${theme.color.neutral[900]};
    }
  }

  ${({ open }) =>
    open &&
    css`
      transform: rotateZ(180deg);

      svg {
        top: 0;
      }
    `}
`;

const Title = styled(H3)`
  font-size: ${toRem(theme.font.size.lg)};
  line-height: ${lineHeight(theme.font.size.lg)};
`;

const Subtitle = styled.div``;

const Count = styled.em`
  font-style: normal;
  font-weight: ${theme.font.weight.regular};
  font-size: inherit;
  line-height: inherit;
  color: inherit;
`;

const Content = styled(motion.section)<Partial<AccordionSectionProps>>`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  border-bottom-right-radius: ${toRem(RADIUS)};
  border-bottom-left-radius: ${toRem(RADIUS)};
  overflow: hidden;
  transform-origin: left top;

  ${({ open }) =>
    open &&
    css`
      overflow: visible;
    `}

  ${({ hideContentOverflow }) =>
    hideContentOverflow &&
    css`
      overflow: hidden;
    `}
`;

const StyledSkeleton = styled(Skeleton)`
  margin-right: ${toRem(theme.space.sm)};
`;

const AccordionSection = ({
  actions,
  afterHeaderCardRow,
  as,
  children,
  className = 'accordion-section',
  compact,
  count,
  filters,
  hideButton,
  hideContentOverflow = false,
  isLoading,
  open = true,
  onOpen,
  subtitle,
  title,
  dataCy = 'site-list-item',
}: AccordionSectionProps) => {
  // UI State
  const [isOpen, setIsOpen] = useState<boolean>(open);

  const handleOpen = () => {
    if (onOpen && !isOpen) {
      onOpen();
    }
    setIsOpen(!isOpen);
  };

  const getTitle = (
    <Title data-cy="header-title">
      <span>{title}</span> {(count || count === 0) && <Count>({count})</Count>}
    </Title>
  );

  return (
    <Layout as={as} afterHeaderCardRow={afterHeaderCardRow} className={className} data-cy={dataCy} compact={compact}>
      <Accordion open={isOpen}>
        <Header open={isOpen} data-cy={`${dataCy}-header`} className={`${className}-header`}>
          <HeaderInner>
            {isLoading ? (
              <>
                <HeaderSection>
                  <StyledSkeleton circle width={33} height={33} />
                  <TitleContainer>
                    <Skeleton width={100} />
                    <Skeleton width={100} />
                  </TitleContainer>
                </HeaderSection>
                <Actions>
                  <Skeleton width={100} />
                </Actions>
              </>
            ) : (
              <>
                <HeaderSection>
                  {!hideButton && <ToggleButton appearance="white" icon="Dropdown" label={isOpen ? 'Close' : 'View More'} onClick={handleOpen} open={isOpen} size="small" dataCy="toggle-button" />}
                  {subtitle ? (
                    <TitleContainer>
                      {getTitle}
                      {subtitle && <Subtitle data-cy="header-subtitle">{subtitle}</Subtitle>}
                    </TitleContainer>
                  ) : (
                    getTitle
                  )}
                </HeaderSection>

                {actions && <Actions>{actions}</Actions>}
              </>
            )}
          </HeaderInner>

          {filters && filters}
        </Header>

        <Content
          open={open}
          hideContentOverflow={hideContentOverflow}
          initial={open}
          animate={{
            height: isOpen ? 'auto' : 0,
          }}
          transition={theme.transition.spring.accordion}
          data-cy="accordion-content"
          className={`${className}-content`}
        >
          {children}
        </Content>
      </Accordion>
    </Layout>
  );
};

export default AccordionSection;
